<script setup lang="ts">
import { ref } from 'vue';

import type { PriceRibbonMonth } from '../priceRibbon';
import { PriceRibbonDayTileType } from '../priceRibbon';

import CalendarTileView from './CalendarTileView.vue';
import PriceCalendarHeader from './PriceCalendarHeader.vue';
import type { CalendarTile } from './models';
import { CalendarTileType, TileSelectionType, PriceMaskType } from './models';

const calendarMatrix: CalendarTile[][] = [
  [
    { type: CalendarTileType.WeekDay, text: 'Mo' },
    { type: CalendarTileType.WeekDay, text: 'Tu' },
    { type: CalendarTileType.WeekDay, text: 'We' },
    { type: CalendarTileType.WeekDay, text: 'Th' },
    { type: CalendarTileType.WeekDay, text: 'Fri' },
    { type: CalendarTileType.WeekDay, text: 'Sa' },
    { type: CalendarTileType.WeekDay, text: 'Su' },
  ],
  [
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.CurrentMonthPastDay, text: '1' },
  ],
  [
    { type: CalendarTileType.CurrentMonthPastDay, text: '2' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '3' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '4' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '5' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '6' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '7' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '8' },
  ],
  [
    { type: CalendarTileType.CurrentMonthPastDay, text: '9' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '10' },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '11',
      price: { type: PriceMaskType.Expensive, value: '90.000' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAlternativeProductDay,
      text: '12',
      price: { type: PriceMaskType.Expensive, value: '66.000' },
      selection: TileSelectionType.NotSelected,
      reason: 'Reason',
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '13',
      price: { type: PriceMaskType.Expensive, value: '73.000' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAlternativeProductDay,
      text: '14',
      price: { type: PriceMaskType.Expensive, value: '88.000' },
      reason: 'Alternative product tile reason',
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '15',
      price: { type: PriceMaskType.Expensive, value: '55.800' },
      selection: TileSelectionType.NotSelected,
    },
  ],
  [
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '16',
      price: { type: PriceMaskType.Expensive, value: '64.244' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '17',
      price: { type: PriceMaskType.Moderate, value: '33.355' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '18',
      price: { type: PriceMaskType.Cheap, value: '6.600' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthUnavailableDay,
      text: '19',
      reason: 'Not available reason!',
      selection: TileSelectionType.Selected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '20',
      price: { type: PriceMaskType.Moderate, value: '7.600' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '21',
      price: { type: PriceMaskType.Expensive, value: '65.000' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '22',
      price: { type: PriceMaskType.Moderate, value: '45.000' },
      selection: TileSelectionType.NotSelected,
    },
  ],
  [
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '23',
    },
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '24',
    },
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '25',
    },
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '26',
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '27',
      price: { type: PriceMaskType.Moderate, value: '206.000' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '28',
    },
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '29',
    },
  ],
  [
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '30',
    },
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '31',
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
  ],
];

const updatedCalendarMatrix: CalendarTile[][] = [
  [
    { type: CalendarTileType.WeekDay, text: 'Mo' },
    { type: CalendarTileType.WeekDay, text: 'Tu' },
    { type: CalendarTileType.WeekDay, text: 'We' },
    { type: CalendarTileType.WeekDay, text: 'Th' },
    { type: CalendarTileType.WeekDay, text: 'Fri' },
    { type: CalendarTileType.WeekDay, text: 'Sa' },
    { type: CalendarTileType.WeekDay, text: 'Su' },
  ],
  [
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.PreviousMonthDay },
    { type: CalendarTileType.CurrentMonthPastDay, text: '1' },
  ],
  [
    { type: CalendarTileType.CurrentMonthPastDay, text: '2' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '3' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '4' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '5' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '6' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '7' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '8' },
  ],
  [
    { type: CalendarTileType.CurrentMonthPastDay, text: '9' },
    { type: CalendarTileType.CurrentMonthPastDay, text: '10' },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '11',
      price: { type: PriceMaskType.Expensive, value: '90.000' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAlternativeProductDay,
      text: '12',
      price: { type: PriceMaskType.Expensive, value: '66.000' },
      selection: TileSelectionType.NotSelected,
      reason: 'Reason',
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '13',
      price: { type: PriceMaskType.Expensive, value: '73.000' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAlternativeProductDay,
      text: '14',
      price: { type: PriceMaskType.Expensive, value: '88.000' },
      reason: 'Alternative product tile reason',
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '15',
      price: { type: PriceMaskType.Expensive, value: '55.800' },
      selection: TileSelectionType.NotSelected,
    },
  ],
  [
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '16',
      price: { type: PriceMaskType.Expensive, value: '64.244' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '17',
      price: { type: PriceMaskType.Moderate, value: '33.355' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '18',
      price: { type: PriceMaskType.Cheap, value: '6.600' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthUnavailableDay,
      text: '19',
      reason: 'Not available reason!',
      selection: TileSelectionType.Selected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '20',
      price: { type: PriceMaskType.Moderate, value: '7.600' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '21',
      price: { type: PriceMaskType.Expensive, value: '65.000' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '22',
      price: { type: PriceMaskType.Moderate, value: '45.000' },
      selection: TileSelectionType.NotSelected,
    },
  ],
  [
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '23',
      price: { type: PriceMaskType.Expensive, value: '64.244' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '24',
      price: { type: PriceMaskType.Moderate, value: '33.355' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '25',
      price: { type: PriceMaskType.Cheap, value: '6.600' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthUnavailableDay,
      text: '26',
      reason: 'Not available reason!',
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '27',
      price: { type: PriceMaskType.Moderate, value: '7.600' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '28',
      price: { type: PriceMaskType.Expensive, value: '65.000' },
      selection: TileSelectionType.NotSelected,
    },
    {
      type: CalendarTileType.CurrentMonthAvailableDay,
      text: '29',
      price: { type: PriceMaskType.Moderate, value: '45.000' },
      selection: TileSelectionType.NotSelected,
    },
  ],
  [
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '30',
    },
    {
      type: CalendarTileType.CurrentMonthLoaderDay,
      text: '31',
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
    {
      type: CalendarTileType.NextMonthDay,
    },
  ],
];

const months: PriceRibbonMonth[] = [
  {
    month: 'January',
    monthShort: 'Jan',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Wed Jan 01 2025',
    days: [],
  },
  {
    month: 'February',
    monthShort: 'Feb',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Sat Feb 01 2025',
    days: [],
  },
  {
    month: 'March',
    monthShort: 'Mar',
    year: '2025',
    isExtended: true,
    firstDayInMonthISO: 'Sat Mar 01 2025',
    days: [
      {
        date: '2025-03-01T01:00:00',
        dayMonth: '1 Mar',
        weekday: 'Saturday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 841 kr',
        },
      },
      {
        date: '2025-03-02T01:00:00',
        dayMonth: '2 Mar',
        weekday: 'Sunday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 299 kr',
        },
      },
      {
        date: '2025-03-03T01:00:00',
        dayMonth: '3 Mar',
        weekday: 'Monday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 584 kr',
        },
      },
      {
        date: '2025-03-04T01:00:00',
        dayMonth: '4 Mar',
        weekday: 'Tuesday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '6 034 kr',
        },
      },
      {
        date: '2025-03-05T01:00:00',
        dayMonth: '5 Mar',
        weekday: 'Wednesday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 980 kr',
        },
      },
      {
        date: '2025-03-06T01:00:00',
        dayMonth: '6 Mar',
        weekday: 'Thursday',
        isSelected: false,
        isSuggested: true,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '4 348 kr',
        },
      },
      {
        date: '2025-03-07T01:00:00',
        dayMonth: '7 Mar',
        weekday: 'Friday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 628 kr',
        },
      },
      {
        date: '2025-03-08T01:00:00',
        dayMonth: '8 Mar',
        weekday: 'Saturday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 776 kr',
        },
      },
      {
        date: '2025-03-09T01:00:00',
        dayMonth: '9 Mar',
        weekday: 'Sunday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 526 kr',
        },
      },
      {
        date: '2025-03-10T01:00:00',
        dayMonth: '10 Mar',
        weekday: 'Monday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 862 kr',
        },
      },
      {
        date: '2025-03-11T01:00:00',
        dayMonth: '11 Mar',
        weekday: 'Tuesday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '6 306 kr',
        },
      },
      {
        date: '2025-03-12T01:00:00',
        dayMonth: '12 Mar',
        weekday: 'Wednesday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '6 046 kr',
        },
      },
      {
        date: '2025-03-13T01:00:00',
        dayMonth: '13 Mar',
        weekday: 'Thursday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 786 kr',
        },
      },
      {
        date: '2025-03-14T01:00:00',
        dayMonth: '14 Mar',
        weekday: 'Friday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 470 kr',
        },
      },
      {
        date: '2025-03-15T01:00:00',
        dayMonth: '15 Mar',
        weekday: 'Saturday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 684 kr',
        },
      },
      {
        date: '2025-03-16T01:00:00',
        dayMonth: '16 Mar',
        weekday: 'Sunday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 526 kr',
        },
      },
      {
        date: '2025-03-17T01:00:00',
        dayMonth: '17 Mar',
        weekday: 'Monday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 862 kr',
        },
      },
      {
        date: '2025-03-18T01:00:00',
        dayMonth: '18 Mar',
        weekday: 'Tuesday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '6 324 kr',
        },
      },
      {
        date: '2025-03-19T01:00:00',
        dayMonth: '19 Mar',
        weekday: 'Wednesday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '6 064 kr',
        },
      },
      {
        date: '2025-03-20T01:00:00',
        dayMonth: '20 Mar',
        weekday: 'Thursday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 804 kr',
        },
      },
      {
        date: '2025-03-21T01:00:00',
        dayMonth: '21 Mar',
        weekday: 'Friday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 470 kr',
        },
      },
      {
        date: '2025-03-22T01:00:00',
        dayMonth: '22 Mar',
        weekday: 'Saturday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 684 kr',
        },
      },
      {
        date: '2025-03-23T01:00:00',
        dayMonth: '23 Mar',
        weekday: 'Sunday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 569 kr',
        },
      },
      {
        date: '2025-03-24T01:00:00',
        dayMonth: '24 Mar',
        weekday: 'Monday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 949 kr',
        },
      },
      {
        date: '2025-03-25T01:00:00',
        dayMonth: '25 Mar',
        weekday: 'Tuesday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '6 521 kr',
        },
      },
      {
        date: '2025-03-26T01:00:00',
        dayMonth: '26 Mar',
        weekday: 'Wednesday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '6 251 kr',
        },
      },
      {
        date: '2025-03-27T01:00:00',
        dayMonth: '27 Mar',
        weekday: 'Thursday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 982 kr',
        },
      },
      {
        date: '2025-03-28T01:00:00',
        dayMonth: '28 Mar',
        weekday: 'Friday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 545 kr',
        },
      },
      {
        date: '2025-03-29T01:00:00',
        dayMonth: '29 Mar',
        weekday: 'Saturday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '5 861 kr',
        },
      },
      {
        date: '2025-03-30T01:00:00',
        dayMonth: '30 Mar',
        weekday: 'Sunday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '6 358 kr',
        },
      },
      {
        date: '2025-03-31T02:00:00',
        dayMonth: '31 Mar',
        weekday: 'Monday',
        isSelected: false,
        isSuggested: false,
        state: {
          type: PriceRibbonDayTileType.LoadedPrice,
          priceWithCurrency: '6 797 kr',
        },
      },
    ],
  },
  {
    month: 'April',
    monthShort: 'Apr',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Tue Apr 01 2025',
    days: [],
  },
  {
    month: 'May',
    monthShort: 'May',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Thu May 01 2025',
    days: [],
  },
  {
    month: 'June',
    monthShort: 'Jun',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Sun Jun 01 2025',
    days: [],
  },
  {
    month: 'July',
    monthShort: 'Jul',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Tue Jul 01 2025',
    days: [],
  },
  {
    month: 'August',
    monthShort: 'Aug',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Fri Aug 01 2025',
    days: [],
  },
  {
    month: 'September',
    monthShort: 'Sep',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Mon Sep 01 2025',
    days: [],
  },
  {
    month: 'October',
    monthShort: 'Oct',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Wed Oct 01 2025',
    days: [],
  },
  {
    month: 'November',
    monthShort: 'Nov',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Sat Nov 01 2025',
    days: [],
  },
  {
    month: 'December',
    monthShort: 'Dec',
    year: '2025',
    isExtended: false,
    firstDayInMonthISO: 'Mon Dec 01 2025',
    days: [],
  },
  {
    month: 'January',
    monthShort: 'Jan',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Thu Jan 01 2026',
    days: [],
  },
  {
    month: 'February',
    monthShort: 'Feb',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Sun Feb 01 2026',
    days: [],
  },
  {
    month: 'March',
    monthShort: 'Mar',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Sun Mar 01 2026',
    days: [],
  },
  {
    month: 'April',
    monthShort: 'Apr',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Wed Apr 01 2026',
    days: [],
  },
  {
    month: 'May',
    monthShort: 'May',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Fri May 01 2026',
    days: [],
  },
  {
    month: 'June',
    monthShort: 'Jun',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Mon Jun 01 2026',
    days: [],
  },
  {
    month: 'July',
    monthShort: 'Jul',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Wed Jul 01 2026',
    days: [],
  },
  {
    month: 'August',
    monthShort: 'Aug',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Sat Aug 01 2026',
    days: [],
  },
  {
    month: 'September',
    monthShort: 'Sep',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Tue Sep 01 2026',
    days: [],
  },
  {
    month: 'October',
    monthShort: 'Oct',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Thu Oct 01 2026',
    days: [],
  },
  {
    month: 'November',
    monthShort: 'Nov',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Sun Nov 01 2026',
    days: [],
  },
  {
    month: 'December',
    monthShort: 'Dec',
    year: '2026',
    isExtended: false,
    firstDayInMonthISO: 'Tue Dec 01 2026',
    days: [],
  },
];

const calendarRef = ref(calendarMatrix);

const timeout = 3000;
setTimeout(() => (calendarRef.value = updatedCalendarMatrix), timeout);

function createTileKey(
  calendarRowIndex: number,
  calendarTileIndex: number
): string {
  return `${calendarRowIndex}-${calendarTileIndex}`;
}

function getSelectedMonthISODate(): string {
  const today = new Date();
  return today.toISOString();
}
</script>

<template>
  <div class="price-calendar">
    <PriceCalendarHeader
      :months="months"
      :selected-month-iso-date="getSelectedMonthISODate()"
    />
    <div>
      <div
        v-for="(calendarRow, calendarRowIndex) of calendarRef"
        :key="calendarRowIndex"
        class="calendar-row"
      >
        <CalendarTileView
          v-for="(calendarTile, calendarTileIndex) of calendarRow"
          :key="createTileKey(calendarRowIndex, calendarTileIndex)"
          :calendar-tile="calendarTile"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.price-calendar {
  --calendar-tile-background-color: var(--color-white);
  --calendar-tile-border-radius: var(--border-radius-8);
  --calendar-tile-day-font-color: var(--color-secondary-40);
  --calendar-tile-day-font: var(--font-label-l-bold);
  --calendar-tile-hover-color: var(--color-secondary-97);
  --calendar-tile-info-icon-right: 6px;
  --calendar-tile-info-icon-top: 1px;
  --calendar-tile-price-font-color: var(--color-secondary-60);
  --calendar-tile-price-font: {
    @include font-label-s-light;
  }
  --calendar-tile-size: 62px;
  --calendar-tile-cheap-color: var(--color-positive-70);
  --calendar-tile-moderate-color: var(--color-tertiary-60);
  --calendar-tile-expensive-color: #ffd2d7; // TODO: Name this -> Almir
  --calendar-weekday-font-color: var(--color-secondary-60);
  --calendar-weekday-font: {
    @include font-label-s-light;
  }

  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;

  .calendar-row {
    display: flex;
  }
}
</style>
